import { lazy, Suspense } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { Box, Button, Flex, Heading, Icon, Link, Text, VStack } from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import { useAuth } from '../hooks/useAuth'
import { useMemberReviewStatus } from '../hooks/useItems'
import useMobile from '../hooks/useMobile'
import { SadFace } from '../utils/constants/icons'
import { cdn } from '../utils/cdn'
import InvalidDisplaySize from '../pages/InvalidDisplaySize'
import Loading2 from '../pages/Loading2'
import { PrivateRoutes } from './PrivateRoutes'
import { PublicRoutes } from './PublicRoutes'
import ErrorBoundary from '../components/common/ErrorBoundary'

const HelpCenter = lazy(() => import('../pages/Help/HelpCenter'))
const MemberReportPdf = lazy(() => import('../pages/Pdf/MemberReportPdf'))
const MemberReportPdfV2 = lazy(() => import('../pages/Pdf/MemberReportPdfV2'))
const MemberReportPdfV3 = lazy(() => import('../pages/Pdf/MemberReportPdfV3'))
const GraphExport = lazy(() => import('../pages/react/Kraken/GraphExport'))
const ChatExport = lazy(() => import('../pages/react/Kraken/ChatExport'))

const AccessDenied = () => {
  const { logout } = useAuth()
  return (
    <Flex w="100%" minH="100vh" pos="relative">
      {/* Left — content */}
      <VStack
        w={{ base: '100%', lg: '50%' }}
        h="100vh"
        p="40px 60px"
        bg="white"
        align="flex-start"
        justify="flex-start"
      >
        <Box
          bgImage={`url('${cdn('/logo/blue-md.svg')}')`}
          w="100px"
          h="40px"
          bgPos="center"
          bgSize="contain"
          bgRepeat="no-repeat"
          mb="40px"
        />
        <VStack spacing="20px" maxW="480px" mx="auto" flex={1} justify="center" align="flex-start">
          <Icon w="56px" h="56px" viewBox="0 0 48 48" color="primary.200">
            <SadFace />
          </Icon>
          <Heading fontSize="24px" fontWeight="500" color="neutral.900">
            No account found
          </Heading>
          <Text color="neutral.500" maxW="380px" fontSize="14px" lineHeight="22px">
            It looks like you don't have an account in Ocean yet. If you believe this is a mistake, please reach out to your organization's administrator or contact us at{' '}
            <Link href="mailto:support@react.org" color="primary.500" fontWeight="500">support@react.org</Link>.
          </Text>
          <Button onClick={logout} variant="solid" size="md" mt="4px">
            Sign in with a different account
          </Button>
        </VStack>
        <Flex w="100%" justifyContent="center">
          <Text fontSize="12px" color="neutral.500">&copy; 2026 Ocean, Inc. All rights reserved.</Text>
        </Flex>
      </VStack>

      {/* Right — background image panel */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        w="50%"
        bgImage={`url('${cdn('/background/auth.jpg')}')`}
        bgPos="center"
        bgSize="cover"
        pos="relative"
      >
        <Box w="100%" h="320px" pos="absolute" bottom="0" bgGradient="linear(to-t, rgba(0,0,0,1), transparent)" />
        <Text fontSize="40px" lineHeight="46px" fontWeight="500" color="white" pos="absolute" bottom="120px" w="100%" p="0px 56px">
          Ocean by React
        </Text>
        <Text fontSize="16px" lineHeight="19px" fontWeight="400" color="white" pos="absolute" bottom="70px" w="100%" p="0px 56px">
          The future of collaboration and automation in online enforcement.
        </Text>
      </Box>
    </Flex>
  )
}

export const Router = () => {
  const isMobile = useMobile(1000)
  const isWeb = !Capacitor.isNativePlatform()
  const isProd = import.meta.env.MODE === 'production'
  const { loading, permissions, accessDenied } = useAuth()
  const location = useLocation()

  const reviewStatus = useMemberReviewStatus(permissions.isMember)

  if (location.pathname.startsWith('/help')) {
    return <Suspense fallback={<div />}><HelpCenter /></Suspense>
  }

  if (location.pathname.startsWith('/graph-export')) {
    return <Suspense fallback={<div />}><GraphExport /></Suspense>
  }

  if (location.pathname.startsWith('/chat-export')) {
    return <Suspense fallback={<div />}><ChatExport /></Suspense>
  }

  if (location.pathname === '/pdf') {
    return <Suspense fallback={<div />}><MemberReportPdf /></Suspense>
  }
  if (location.pathname === '/pdf/v2') {
    return <Suspense fallback={<div />}><MemberReportPdfV2 /></Suspense>
  }
  if (location.pathname === '/pdf/v3') {
    return <Suspense fallback={<div />}><MemberReportPdfV3 /></Suspense>
  }

  if (isWeb && isProd && isMobile) return <InvalidDisplaySize />
  if (accessDenied) return <AccessDenied />
  if (loading.query) return <Loading2 />

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public Routes */}
        {PublicRoutes()}

        {/* Private Routes */}
        {reviewStatus && permissions !== undefined && PrivateRoutes({ reviewStatus, permissions })}
      </Routes>
    </ErrorBoundary>
  )
}
